import React from 'react';
import PropTypes from 'prop-types';
import { useForm, Controller } from 'react-hook-form';
import intersection from 'lodash/intersection';
import { yupResolver } from '@hookform/resolvers/yup';
import { RoundedCheckbox, Banner, MultiSelect } from '@web/atoms';
import { StyledForm } from '../StyledForm';
import { FormFieldsContainer } from '../FormFieldsContainer';
import { useAllergies, useGetAllergies } from '../../allergies';
import { ApiResult } from '../../api';
import { useReporter } from '../../reporter';
import { useSessionCustomer } from '../../customer';
import { onboardingSubmitted } from '../events';
import { Footer } from '../Footer';
import { Question } from '../Question';
import { CheckboxContainer, MultiSelectContainer } from './styles';
import { schema } from './schema';
import { getPlaceholderAllergies } from './constants';

export { schema } from './schema';

const toggleFeaturedAllergy = (allergy, state) => {
  if (!findAllergyInState(allergy, state)) {
    return [...state, allergy];
  }
  return state.filter(a => a.value !== allergy.value);
};

const findAllergyInState = (allergy, state) =>
  state.find(a => a.value === allergy.value);

const UNUSED_INGREDIENTS = ['Wheat', 'Corn', 'Soy'];

const normalize = allergies =>
  allergies.map(allergy => ({
    label: allergy.name,
    value: allergy.id,
  }));

export const AllergiesForm = ({
  allergiesQuestionPrompt,
  allergiesQuestionCaption,
  defaultValues,
  goBack,
  onSubmit,
  index,
  allergySearchLabel,
  unusedIngredients = UNUSED_INGREDIENTS,
  placeholderAllergiesCount,
  petPronoun,
  ctaButtonText,
}) => {
  const reporter = useReporter();
  const getAllergies = useGetAllergies();
  const { customer } = useSessionCustomer();
  const allergies = useAllergies();
  const {
    handleSubmit,
    control,
    watch,
    formState: { isSubmitting, isSubmitted },
    reset,
    getValues,
  } = useForm({
    defaultValues,
    mode: 'onChange',
    resolver: yupResolver(schema),
  });

  const placeholderAllergies = getPlaceholderAllergies(
    placeholderAllergiesCount,
  );
  const featuredAllergies = normalize(allergies ?? placeholderAllergies);

  const featuredAllergiesValues = featuredAllergies?.map(({ value }) => value);

  const selectedAllergies = watch('allergies');

  const selectedFeaturedAllergies = selectedAllergies.filter(a =>
    featuredAllergiesValues.includes(a.value),
  );

  const multiselectOnChange = multiselectedValues => [
    ...multiselectedValues,
    ...selectedFeaturedAllergies,
  ];

  let selectedExcludedIngredients = intersection(
    unusedIngredients,
    selectedAllergies.map(a => a.label),
  );

  const excludedIngredientsCopy = (() => {
    selectedExcludedIngredients = selectedExcludedIngredients.map(i =>
      i.toLowerCase(),
    );
    if (selectedExcludedIngredients.length > 1) {
      const last = selectedExcludedIngredients.pop();
      return `Good news! Ollie recipes are always free from ${selectedExcludedIngredients.join(
        ', ',
      )} and ${last}.`;
    } else if (selectedExcludedIngredients.length === 1) {
      return `Good news! Ollie recipes are always free from ${selectedExcludedIngredients[0]}.`;
    }
    return null;
  })();

  const defaultCopy =
    !excludedIngredientsCopy && selectedAllergies.length === 0
      ? `If ${petPronoun} has no food allergies, click Continue`
      : null;

  const submitForm = async data => {
    reporter.tag(onboardingSubmitted(index, 'dog_allergies', customer));
    const result = await onSubmit(data);

    ApiResult.ifError(result, () =>
      setTimeout(() =>
        reset(getValues(), {
          keepIsSubmitted: false,
        }),
      ),
    );
  };

  return (
    <StyledForm onSubmit={handleSubmit(submitForm)}>
      <FormFieldsContainer $maxWidth={391}>
        <Question
          prompt={allergiesQuestionPrompt}
          subheadline={allergiesQuestionCaption}
        >
          <Controller
            control={control}
            name="allergies"
            render={({ field: { onChange } }) => (
              <>
                <CheckboxContainer>
                  {featuredAllergies.map(allergy => (
                    <RoundedCheckbox
                      {...allergy}
                      key={allergy.value}
                      checked={!!findAllergyInState(allergy, selectedAllergies)}
                      onChange={() =>
                        onChange(
                          toggleFeaturedAllergy(allergy, selectedAllergies),
                        )
                      }
                      showIcon={
                        findAllergyInState(allergy, selectedAllergies)
                          ? 'check'
                          : null
                      }
                      label={allergy.label}
                    />
                  ))}
                </CheckboxContainer>

                <MultiSelectContainer>
                  <MultiSelect
                    name="other allergies"
                    selectedValues={selectedAllergies}
                    featuredValues={featuredAllergies}
                    values={getAllergies}
                    onChange={values => onChange(multiselectOnChange(values))}
                    searchLabel={allergySearchLabel}
                  />
                </MultiSelectContainer>
              </>
            )}
          />
        </Question>
        {defaultCopy && (
          <Banner $bgColor="background.base">{defaultCopy}</Banner>
        )}
        {excludedIngredientsCopy && (
          <Banner $bgColor="background.base">{excludedIngredientsCopy}</Banner>
        )}
      </FormFieldsContainer>

      <Footer
        mobileCTAText={ctaButtonText?.mobileCTA}
        desktopCTAText={ctaButtonText?.desktopCTA}
        isBackButtonDisabled={isSubmitting || isSubmitted}
        isForwardButtonDisabled={isSubmitting || isSubmitted}
        onBackButtonClick={goBack}
        isForwardButtonLoading={isSubmitting || isSubmitted}
      />
    </StyledForm>
  );
};

AllergiesForm.displayName = 'AllergiesForm';

AllergiesForm.propTypes = {
  allergiesQuestionPrompt: PropTypes.string.isRequired,
  allergiesQuestionCaption: PropTypes.string,
  defaultValues: PropTypes.shape({
    selectedAllergies: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string.isRequired,
        value: PropTypes.string.isRequired,
      }),
    ),
  }).isRequired,
  goBack: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  index: PropTypes.number.isRequired,
  allergySearchLabel: PropTypes.string.isRequired,
  unusedIngredients: PropTypes.arrayOf(PropTypes.string),
  placeholderAllergiesCount: PropTypes.number.isRequired,
  petPronoun: PropTypes.string.isRequired,
  ctaButtonText: PropTypes.object,
};
